import React, { useContext } from 'react';
import { AppContext } from '../../../AppContext';
import styles from './styles.module.css';
import SyntaxHighlighter from '../../../components/SyntaxHighlighter'
import { data } from './data';
import ProTip from '../../../components/ProTip';

const lineStyle={marginLeft:-20,fontSize:16,color:'gray'}

const BubbleChart=props=>{
    const { isMobile } = props;
    const appContext = useContext(AppContext);
    const { theme } = appContext;
    return(
        <div className={styles.container} style={{ paddingLeft: isMobile ? 3 : 20 }}>
            <div style={{maxWidth: 1100, marginRight: isMobile ? 0 : 130}}>
            <h2>
                Bubble Charts
                <a style={{marginLeft:20,fontSize:15, color: theme==='dark'? 'skyblue' : 'blue'}} target="_blank" href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/BubbleChart/BubbleChartProps.md' rel="noreferrer">
                    props table
                </a>
            </h2>
            <div style={{marginLeft:10,marginBottom:100}}>
                <p>
                    Bubble chart is similar to a line chart but represents data points as bubbles, where the size of each bubble indicates an additional dimension of the data.
                </p>
                <h4>
                    Minimal Bubble Chart
                </h4>
                <div style={{marginLeft:10}}>
                    <img
                        src={data[0].imgSrc}
                        className={styles.barOutput}
                        alt=''/>
                    <p>
                        The code for the above chart is-
                    </p>
                    <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="javascript">
                        {data[0].code}
                    </SyntaxHighlighter>
                </div>

                <p>
                    The size can be specified using the <b>r</b> attribute in the data points.
                    Below is an example of a bubble chart with varying bubble sizes.
                </p>
                <div style={{marginLeft:10}}>
                    <img
                        src={data[1].imgSrc}
                        className={styles.barOutput}
                        alt=''/>
                    <p>
                        The code for the above chart is-
                    </p>
                    <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="javascript">
                        {data[1].code}
                    </SyntaxHighlighter>
                    <p> 
                        If we want to set the general size of all bubbles, we can use the <b>bubblesRadius</b> prop in the 
                        <b>BubbleChart</b> component.
                        This sets the radius for all bubbles uniformly. Then we can still override individual bubble sizes using the <b>r</b> attribute in the data points.
                    </p>
                </div>
                
                <h4>
                    label
                </h4>
                <p>
                    We can use the prop <b>showValuesAsBubbleLabels</b> to display the values as labels inside the bubbles.
                </p>
                <div style={{marginLeft:10}}>
                    <img
                        src={data[2].imgSrc}
                        className={styles.barOutput}
                        alt=''/>
                    <p>
                        The code for the above chart is-
                    </p>
                    <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="javascript">
                        {data[2].code}
                    </SyntaxHighlighter>
                    <p>
                        We can also adjust the font size of the bubble labels using the <b>labelFontSize</b> prop. <br />
                    </p>
                    <p> label related props-
                        <ol>
                            <li>
                                <b>labelFontSize</b> - The font size of the bubble labels.
                            </li>
                            <li>
                                <b>showValuesAsBubbleLabels</b> - To display the y value of each bubble as bubble labels.
                            </li>
                            <li>
                                <b>labelTextStyle</b> - The style of the bubble labels.
                            </li>
                            <li>
                                <b>labelComponent</b> - Custom label component rendered inside the bubbles.
                            </li>
                        </ol>
                    </p>
                </div>

                <ProTip
                    body={() => {
                        return (
                        <>
                        The props of Bubble chart are similar to those of Line chart. The <b>r</b> in the data array is analagous to <b>bubbleRadius</b> in the Line chart.
                        </>
                        )
                    }}
                />

                <h4>
                    Gradient
                </h4>
                <p>
                    Gradient colors can be set using the <b>centerColorForGradient</b> and the regular <b>bubblesColor</b> props. <br />
                    Also these values can be set inside data array for individual items.
                </p>

                <hr />

                <h4>
                    Regression Line
                </h4>
                <p>
                    The regression line can be displayed on the bubble chart by setting the <b>showRegressionLine</b> prop to true in the data set. The appearance and behavior of the regression line can be customized using the <b>regressionLineConfig</b> prop.
                </p>
                <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="typescript">
                    {data[3].code}
                </SyntaxHighlighter>

                <h4>
                    dataSet
                </h4>
                <p>
                    Similar to Line charts, You can pass an array of data using the <b>dataSet</b> prop. dataSet is an array of objects of type <b>DataSetForLineChart</b> described below. <br />
                    For Bubble chart, the corresponding type is <b>DataSetForBubbleChart</b> described below-
                </p>
                <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="typescript">
                    {data[4].code}
                </SyntaxHighlighter>

                <h4>
                    X and Y Coordinate Positioning
                </h4>

                <p>
                    The BubbleChart uses a coordinate system where:
                    <ul>
                        <li><b>Y coordinate</b>: Always required. The <i>y</i> value represents the vertical position in data space and is converted to screen coordinates using the <i>getY()</i> function based on <i>maxY</i>, <i>containerHeight</i>, and other Y-axis configuration.</li>
                        <br/>
                        <li><b>X coordinate</b>: Optional. The X positioning works as follows:
                            <br /><br />
                            <ul>
                                <li><b>If `x` is provided</b>: The bubble's X position is calculated as `x * xScale`, where `xScale` is the scaling factor that converts data space X values to screen coordinates.</li>
                                <li><b>If `x` is not provided</b>: The X position is auto-calculated based on the bubble's index, evenly distributing bubbles across the chart width while accounting for the bubble's radius to prevent overflow.</li>
                            </ul>
                        </li>
                    </ul>
                    The <b>getX()</b> function implementation:
                    <SyntaxHighlighter showLineNumbers lineNumberStyle={lineStyle} language="typescript">
                    {data[5].code}
                </SyntaxHighlighter>
                </p>
                <p>
                    This allows you to either:
                    <ol>
                        <li><b>Use explicit X values</b>: Provide <i>x</i> values in your data for precise positioning (useful for scatter plots or when you need specific X coordinates).</li>
                        <li><b>Use automatic spacing</b>: Omit <i>x</i> values to let the chart automatically space bubbles evenly across the available width.</li>
                    </ol>
                </p>

            </div>
            </div>
        </div>
    )
}

export default BubbleChart;